import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AiOutlineLoading } from "react-icons/ai";
import {
  HiArrowRight,
  HiCheckCircle,
  HiExclamationCircle,
  HiOutlineMail,
} from "react-icons/hi";

import { Button, cn } from "../../ui";
import AuthShell from "./components/AuthShell";
import dosee_logo from "../../../assets/images/dosee.png";
import { useAdminContext } from "../../context/admin/AdminContext";

type Feedback = { type: "success" | "error"; text: string } | null;

const VerificationInstructions: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { resendOtp } = useAdminContext();

  const email = (location.state as { email?: string } | null)?.email ?? "your email";

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const handleResend = async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const response = await resendOtp({ email });
      if (response.status === 200) {
        setFeedback({ type: "success", text: "New code sent successfully." });
      } else {
        setFeedback({
          type: "error",
          text: response.message || "Failed to resend code. Please try again.",
        });
      }
    } catch {
      setFeedback({ type: "error", text: "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell eyebrow="Verify email">
      <div className="mb-6 flex items-center justify-center lg:hidden">
        <img src={dosee_logo} alt="DoseeAfrica" className="h-10 w-auto" />
      </div>

      <div className="mb-7 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10 text-brand ring-1 ring-inset ring-brand/20">
        <HiOutlineMail className="h-6 w-6" />
      </div>

      <h1 className="text-[28px] font-semibold leading-tight tracking-tight text-gray-900">
        Check your inbox
      </h1>
      <p className="mt-2 text-[13.5px] leading-relaxed text-gray-600">
        We sent a verification code to{" "}
        <span className="font-medium text-gray-900">{email}</span>. Enter it on
        the next screen to activate your account.
      </p>

      {feedback ? (
        <div
          className={cn(
            "mt-5 flex items-start gap-2 rounded-xl border px-3.5 py-2.5 text-[12.5px]",
            feedback.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-red-200 bg-red-50 text-red-700",
          )}
          role="status"
        >
          {feedback.type === "success" ? (
            <HiCheckCircle className="mt-0.5 h-4 w-4 text-brand" />
          ) : (
            <HiExclamationCircle className="mt-0.5 h-4 w-4 text-red-600" />
          )}
          <span>{feedback.text}</span>
        </div>
      ) : null}

      <div className="mt-7 space-y-3">
        <Button
          type="button"
          variant="primary"
          className="w-full"
          onClick={() => navigate("/verify-otp", { state: { email } })}
        >
          Enter verification code
          <HiArrowRight className="h-4 w-4" />
        </Button>

        <Button
          type="button"
          variant="secondary"
          className="w-full"
          onClick={handleResend}
          disabled={loading}
        >
          {loading ? (
            <>
              <AiOutlineLoading className="h-4 w-4 animate-spin" /> Sending new code…
            </>
          ) : (
            "Resend verification code"
          )}
        </Button>
      </div>

      <p className="mt-6 text-[12px] text-gray-500">
        Wrong email?{" "}
        <Link
          to="/signup"
          className="font-medium text-gray-700 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-700"
        >
          Register again
        </Link>
      </p>
    </AuthShell>
  );
};

export default VerificationInstructions;
